import { Icon, type DraggableListItemProps } from "@mdotm/shadcn/legacy/components";
import { type NodeOrFn, toClassName, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { CommonConstraintData } from "../shared";

export const editConstraintTitleClassName = `text-[color:var(--global-palette-neutral-700)] pb-2`;
type ExtendedCommonConstraintData = CommonConstraintData & { _id?: string };
export type DefaultConstraintListItemProps<Constraint extends ExtendedCommonConstraintData> = {
	onChange(newItem: Constraint): void;
	onRemove(id: string): void;
	draggableListItemProps?: DraggableListItemProps<Constraint>;
	item: Constraint;
	children: NodeOrFn;
	actions?: boolean;
	subItem?: NodeOrFn;
	disableActions?: Array<"DELETE" | "EDIT">;
};

export function DefaultConstraintListItem<Constraint extends ExtendedCommonConstraintData>({
	item,
	draggableListItemProps,
	onChange,
	onRemove,
	children,
	actions = true,
	disableActions = [],
	subItem,
}: DefaultConstraintListItemProps<Constraint>): React.JSX.Element {
	return (
		<div
			className={toClassName({
				[`w-full pl-4 pr-5 py-4 min-h-[86px] border-b border-b-[var(--global-palette-neutral-200)] bg-[var(--global-palette-neutral-0)]`]: true,
				"shadow-none border-b": !draggableListItemProps?.isDragging,
				"shadow-lg border": draggableListItemProps?.isDragging,
			})}
			{...draggableListItemProps?.draggableProps}
			ref={draggableListItemProps?.innerRef}
		>
			<div className="flex justify-between items-start">
				{draggableListItemProps && (
					<div {...draggableListItemProps?.dragHandleProps} className="pr-4 pt-4">
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M17.9702 9.07278L16.5243 7.48877C16.3862 7.34255 16.2075 7.2532 16.0125 7.2532C15.8176 7.2532 15.6307 7.31818 15.4845 7.45628C15.1921 7.74059 15.1677 8.22798 15.4439 8.52853L15.9557 9.10528C15.9557 9.10528 16.0206 9.18651 16.0694 9.24337H10.7731V3.92271C10.8299 3.97145 10.8787 4.01206 10.9112 4.03643L11.4554 4.54007C11.7641 4.81625 12.2434 4.80813 12.5277 4.50757C12.6658 4.36136 12.7389 4.17453 12.7389 3.97957C12.7389 3.78461 12.6495 3.59778 12.4952 3.45969L10.8949 2.01377C10.3832 1.55075 9.5871 1.55075 9.07535 2.01377L7.49133 3.45969C7.34512 3.59778 7.25576 3.77649 7.25576 3.97957C7.25576 4.17453 7.32075 4.36136 7.45884 4.50757C7.74315 4.80813 8.23054 4.82438 8.5311 4.54819L9.10784 4.03643C9.10784 4.03643 9.18907 3.97145 9.24593 3.92271V9.24337H3.91715C3.96589 9.18651 4.0065 9.13777 4.03087 9.10528L4.54263 8.52853C4.81882 8.21985 4.80257 7.74059 4.50202 7.45628C4.3558 7.31818 4.18521 7.2532 3.97401 7.2532C3.77906 7.2532 3.59222 7.34255 3.46225 7.48877L2.01633 9.07278C1.55331 9.58454 1.54519 10.3806 2.01633 10.8924L3.46225 12.4926C3.60035 12.6388 3.77906 12.7282 3.98213 12.7363C4.19334 12.7363 4.37205 12.6713 4.51014 12.5251C4.80257 12.2408 4.81882 11.7534 4.54263 11.4529L4.039 10.9086C4.039 10.9086 3.97401 10.8274 3.92527 10.7705H9.23781V16.0749C9.18095 16.0262 9.13221 15.9856 9.09972 15.9612L8.52297 15.4495C8.21429 15.1733 7.73503 15.1895 7.45072 15.4901C7.31262 15.6363 7.23952 15.8231 7.24764 16.0181C7.24764 16.213 7.33699 16.3999 7.48321 16.538L9.06722 17.9839C9.31904 18.2194 9.65209 18.3332 9.97702 18.3332C10.3019 18.3332 10.6269 18.2194 10.8868 17.9839L12.4871 16.538C12.6333 16.3999 12.7226 16.2212 12.7308 16.0181C12.7308 15.8231 12.6658 15.6282 12.5196 15.4901C12.2353 15.1976 11.7479 15.1814 11.4473 15.4576L10.9031 15.9612C10.9031 15.9612 10.8218 16.0262 10.765 16.0749V10.7786H16.0613C16.0125 10.8355 15.9719 10.8842 15.9475 10.9167L15.4439 11.461C15.1677 11.7615 15.1758 12.2489 15.4764 12.5332C15.6226 12.6713 15.8257 12.7444 16.0044 12.7444C16.1993 12.7444 16.3862 12.6551 16.5243 12.5008L17.9702 10.9005C18.4332 10.3887 18.4332 9.59266 17.9702 9.08091V9.07278Z"
								fill="#4CB09C"
							/>
						</svg>
					</div>
				)}

				<div className="flex flex-1">{renderNodeOrFn(children)}</div>

				<div className="flex space-x-4 pt-6 pl-4">
					{actions && !disableActions.includes("EDIT") && (
						<button
							disabled={!item.readonly}
							className={toClassName({ invisible: !item.readonly })}
							type="button"
							onClick={() => onChange({ ...item, readonly: false })}
						>
							<Icon icon="Edit" color={themeCSSVars.Button_bg_primary} size={21} />
						</button>
					)}
					{actions && !disableActions.includes("DELETE") && (
						<button type="button" onClick={() => onRemove(item.id)}>
							<Icon icon="Delete" color={themeCSSVars.Button_bg_primary} size={21} />
						</button>
					)}
				</div>
			</div>
			{renderNodeOrFn(subItem)}
		</div>
	);
}
